import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Award, ShieldCheck, MapPin, Upload, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { FadeUp } from "../components/Motion";
import PageMeta from "../components/PageMeta";
import { getLeadTracking, trackLeadDeduped } from "../lib/fbpixel";
import { trackGaLead } from "../lib/ga";
import ConsentField from "../components/ConsentField";
import { trackFormStart } from "../lib/formTracking";

const SERVICES = [
  "Full Design Services", "Redesign & Refresh", "Paver Patios", "Retaining Walls",
  "Trees & Shrubs", "Mulch & Stone", "Sod & Turf", "Grading & Top Soil",
  "Flower Beds", "Yard Maintenance", "Decks", "Fences", "Pergolas",
  "Fire Features", "Outdoor Kitchens", "Lighting", "Artificial Turf", "Irrigation",
];

const TIMELINES = [
  "ASAP (within 4 weeks)",
  "Within 1–3 months",
  "Within 3–6 months",
  "Just exploring",
];

const BUDGETS = [
  "Under $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000+",
  "Not sure yet",
];

const SOURCES = ["Google", "Instagram", "Facebook", "Friend / Word of Mouth", "Drove past a job", "Other"];

const PROVINCES = ["Alberta", "British Columbia", "Saskatchewan", "Manitoba", "Ontario", "Other"];

export default function Estimate() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    street: "", city: "Edmonton", province: "Alberta", postal: "",
    services: [],
    timeline: "",
    budget: "",
    source: "",
    source_other: "",
    notes: "",
    photos: [], // [{name, dataUrl}]
    marketing_consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleService = (s) =>
    setForm((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));

  // Compress + add a photo to the form (multi-file aware)
  const addPhotos = (files) => {
    const remaining = 6 - form.photos.length;
    Array.from(files).slice(0, remaining).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1600;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            const ratio = Math.min(MAX / width, MAX / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const c = document.createElement("canvas");
          c.width = width;
          c.height = height;
          c.getContext("2d").drawImage(img, 0, 0, width, height);
          const dataUrl = c.toDataURL("image/jpeg", 0.82);
          setForm((p) => ({ ...p, photos: [...p.photos, { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: file.name, dataUrl }] }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    if (files.length > remaining) {
      toast.info(`Maximum 6 photos. Added the first ${remaining}.`);
    }
  };

  const removePhoto = (idx) =>
    setForm((p) => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const tracking = getLeadTracking();
      await api.post("/leads", {
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone,
        address: [form.street, form.city, form.province, form.postal].filter(Boolean).join(", "),
        message: form.notes,
        lead_type: "residential",
        source_page: "estimate-form",
        marketing_consent: form.marketing_consent,
        metadata: {
          services: form.services,
          timeline: form.timeline,
          budget: form.budget,
          source: form.source === "Other" ? form.source_other : form.source,
          first_name: form.first_name,
          last_name: form.last_name,
          property: {
            street: form.street, city: form.city,
            province: form.province, postal: form.postal,
          },
          photos: form.photos.map((p) => ({
            name: p.name,
            base64: p.dataUrl.split(",")[1],
          })),
        },
        ...tracking,
      });
      trackLeadDeduped(tracking.event_id, {
        content_category: "residential",
        source_page: "estimate-form",
        value: form.budget || undefined,
      });
      trackGaLead({ lead_type: "residential", source_page: "estimate-form" });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Submission failed. Please try again or call (780) 655-7604.");
    } finally { setLoading(false); }
  };

  const input = "w-full bg-[#151515] border border-[#D8D6D0]/20 px-4 py-3 text-sm text-[#F5F3EF] placeholder:text-[#D8D6D0]/40 focus:outline-none focus:border-[#F5F3EF]/70 transition-colors duration-300";
  const label = "overline text-[#D8D6D0]/60 block mb-2";
  const section = "border-t border-[#D8D6D0]/10 pt-12 mt-12";

  if (submitted) {
    return (
      <div data-testid="estimate-success" className="pt-40 pb-32 px-6 md:px-12 lg:px-16 min-h-screen">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex w-16 h-16 items-center justify-center border border-[#8C1D30] bg-[#8C1D30]/20 mb-8">
            <Check className="w-7 h-7 text-[#8C1D30]" strokeWidth={1.5} />
          </div>
          <div className="overline text-[#8C1D30] mb-4">Request Received</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-8">
            Thank you,<br/><span className="italic text-[#D8D6D0]">{form.first_name}.</span>
          </h1>
          <p className="text-[#D8D6D0]/70 leading-relaxed text-lg">
            Your estimate request is in. A senior designer at NorthCrest will reach out within <span className="text-[#F5F3EF]">24 business hours</span> to schedule a complimentary on-site consultation.
          </p>
          {form.email.trim() && (
            <p className="text-[#D8D6D0]/50 text-sm mt-6">
              A confirmation has been sent to <span className="text-[#F5F3EF]">{form.email}</span>.
            </p>
          )}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
            <a href="tel:+17806557604" className="px-4 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-xs uppercase tracking-widest">Call us</a>
            <button onClick={() => nav("/projects")} className="px-4 py-3 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors">View projects</button>
            <button onClick={() => nav("/yard-designer")} className="px-4 py-3 border border-[#D8D6D0]/30 text-[#F5F3EF] text-xs uppercase tracking-widest hover:bg-[#F5F3EF] hover:text-[#151515] transition-colors">AI Designer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="estimate-page" className="pt-28 md:pt-32 pb-32">
      <PageMeta
        title="Get a Free Landscaping Estimate — Edmonton, Alberta"
        description="Request a free, no-obligation landscape estimate from NorthCrest Landscaping in Edmonton. Tell us your project, timeline, and budget — we'll respond within 24 hours."
        path="/estimate"
      />
      {/* HEADER */}
      <section className="px-6 md:px-12 lg:px-16 mb-12 md:mb-20">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Free Estimate</div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-none max-w-5xl">
            Tell us about <span className="italic text-[#D8D6D0]">your project.</span>
          </h1>
          <p className="text-[#D8D6D0]/60 mt-8 max-w-2xl text-lg leading-relaxed">
            A senior designer at NorthCrest will reach out within 24 business hours to schedule a complimentary on-site consultation. No deposit. No obligation.
          </p>
        </FadeUp>

        {/* Trust strip */}
        <FadeUp delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-6 text-xs text-[#D8D6D0]/60">
            <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#8C1D30]" /> 200+ Projects Completed</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#8C1D30]" /> Licensed & Insured</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#8C1D30]" /> Edmonton & Surrounding Areas</div>
          </div>
        </FadeUp>
      </section>

      {/* FORM */}
      <section className="px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <FadeUp delay={0.05} className="lg:col-span-8">
          <form data-testid="estimate-form" onSubmit={submit} className="nc-card p-6 md:p-12">
            {/* ---------- 01 CONTACT ---------- */}
            <div>
              <div className="flex items-baseline justify-between mb-8">
                <div className="overline text-[#D8D6D0]/50">01 — Contact</div>
                <div className="text-xs text-[#D8D6D0]/30">Only name & phone required</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>First Name *</label>
                  <input required data-testid="est-first-name" className={input} value={form.first_name} onFocus={() => trackFormStart("estimate")} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Last Name</label>
                  <input data-testid="est-last-name" className={input} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Phone *</label>
                  <input required minLength={7} pattern="[0-9\s\(\)\+\.\-]{7,}" title="Please enter a valid phone number" data-testid="est-phone" placeholder="(780) ..." className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input type="email" data-testid="est-email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="mt-6">
                <ConsentField checked={form.marketing_consent} onChange={(v) => setForm({ ...form, marketing_consent: v })} testId="est-consent-marketing" />
              </div>
            </div>

            {/* ---------- QUICK ESTIMATE / ACCURACY CALLOUT ---------- */}
            <div data-testid="est-accuracy-note" className="mt-12 border-l-4 border-[#8C1D30] bg-[#8C1D30]/10 px-6 py-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="est-quick-submit"
                  className="inline-flex justify-center items-center px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] disabled:opacity-60 text-[#F5F3EF] text-sm tracking-widest uppercase transition-colors whitespace-nowrap"
                >
                  {loading ? "Sending..." : "Get Quick Estimate"}
                </button>
                <p className="text-sm text-[#D8D6D0]/80 leading-relaxed">
                  <span className="text-[#8C1D30] font-semibold uppercase tracking-wider">or</span>{" "}
                  fill in the full form below — address, services, budget and photos — for a{" "}
                  <span className="text-[#F5F3EF] font-semibold">more accurate estimate</span> and a better-prepared designer visit.
                </p>
              </div>
            </div>

            {/* ---------- 02 PROPERTY ---------- */}
            <div className={section}>
              <div className="overline text-[#D8D6D0]/50 mb-8">02 — Property Address <span className="text-[#D8D6D0]/30 normal-case">(optional)</span></div>
              <div className="space-y-4">
                <div>
                  <label className={label}>Street Address</label>
                  <input data-testid="est-street" className={input} value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={label}>City</label>
                    <input data-testid="est-city" className={input} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                  <div>
                    <label className={label}>Province</label>
                    <select data-testid="est-province" className={input} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}>
                      {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>Postal Code</label>
                    <input data-testid="est-postal" placeholder="T5K 0L4" className={input} value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              </div>
            </div>

            {/* ---------- 03 SERVICES ---------- */}
            <div className={section}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="overline text-[#D8D6D0]/50">03 — Services Needed <span className="text-[#D8D6D0]/30 normal-case">(optional)</span></div>
                <div className="text-xs text-[#D8D6D0]/30">{form.services.length} selected</div>
              </div>
              <p className="text-sm text-[#D8D6D0]/50 mb-6">Select all that apply.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SERVICES.map((s) => {
                  const on = form.services.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      data-testid={`est-service-${s.toLowerCase().replace(/\W+/g, "-")}`}
                      onClick={() => toggleService(s)}
                      className={`px-3 py-3 text-xs text-left transition-colors border ${
                        on ? "bg-[#0B2B1F] border-[#F5F3EF]/60 text-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"
                      }`}
                    >
                      <span className="inline-block w-3 mr-2 text-[#8C1D30]">{on ? "✓" : ""}</span>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ---------- 04 TIMELINE ---------- */}
            <div className={section}>
              <div className="overline text-[#D8D6D0]/50 mb-6">04 — When do you want to start? <span className="text-[#D8D6D0]/30 normal-case">(optional)</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {TIMELINES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`est-timeline-${t.split(" ")[0].toLowerCase()}`}
                    onClick={() => setForm({ ...form, timeline: t })}
                    className={`px-4 py-3 text-sm text-left transition-colors border ${
                      form.timeline === t ? "bg-[#0B2B1F] border-[#F5F3EF]/60 text-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* ---------- 05 BUDGET ---------- */}
            <div className={section}>
              <div className="overline text-[#D8D6D0]/50 mb-6">05 — Approximate Budget <span className="text-[#D8D6D0]/30 normal-case">(optional)</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    data-testid={`est-budget-${b.replace(/\W+/g, "-")}`}
                    onClick={() => setForm({ ...form, budget: b })}
                    className={`px-4 py-3 text-sm text-left transition-colors border ${
                      form.budget === b ? "bg-[#0B2B1F] border-[#F5F3EF]/60 text-[#F5F3EF]" : "border-[#D8D6D0]/20 text-[#D8D6D0]/70 hover:border-[#D8D6D0]/50"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {/* ---------- 06 DETAILS ---------- */}
            <div className={section}>
              <div className="overline text-[#D8D6D0]/50 mb-6">06 — Project Details</div>
              <textarea
                data-testid="est-notes"
                rows={5}
                placeholder="Tell us about your vision — slopes, sun exposure, existing features, style inspiration, anything we should know."
                className={input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            {/* ---------- 07 PHOTOS ---------- */}
            <div className={section}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="overline text-[#D8D6D0]/50">07 — Photos of Your Yard</div>
                <div className="text-xs text-[#D8D6D0]/30">{form.photos.length}/6 uploaded</div>
              </div>
              <p className="text-sm text-[#D8D6D0]/50 mb-6">
                Optional — upload up to 6 photos. Helps our designer plan accurately before the site visit.
              </p>

              {/* Drop zone / file picker */}
              {form.photos.length < 6 && (
                <label
                  className="block border border-dashed border-[#D8D6D0]/30 hover:border-[#F5F3EF]/60 cursor-pointer transition-colors p-8 text-center"
                  data-testid="est-photo-dropzone"
                >
                  <Upload className="w-6 h-6 mx-auto text-[#D8D6D0]/50 mb-3" strokeWidth={1.25} />
                  <div className="text-sm text-[#F5F3EF]">Tap or drag photos here</div>
                  <div className="text-xs text-[#D8D6D0]/50 mt-1">JPG, PNG, HEIC · auto-compressed to ~200 KB each</div>
                  <input
                    data-testid="est-photo-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => addPhotos(e.target.files)}
                    className="hidden"
                  />
                </label>
              )}

              {/* Previews */}
              {form.photos.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="est-photo-previews">
                  {form.photos.map((p, i) => (
                    <div key={p.id || `${p.name}-${i}`} className="relative group border border-[#D8D6D0]/15">
                      <img src={p.dataUrl} alt={p.name} className="w-full h-32 object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        data-testid={`est-photo-remove-${i}`}
                        aria-label="Remove photo"
                        className="absolute top-2 right-2 w-7 h-7 bg-[#151515]/90 hover:bg-[#8C1D30] text-[#F5F3EF] flex items-center justify-center transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-[#151515]/80 text-[10px] text-[#D8D6D0]/70 truncate">
                        {p.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ---------- 08 SOURCE ---------- */}
            <div className={section}>
              <div className="overline text-[#D8D6D0]/50 mb-6">08 — How did you hear about us?</div>
              <select data-testid="est-source" className={input} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                <option value="">Please choose…</option>
                {SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
              {form.source === "Other" && (
                <input
                  data-testid="est-source-other"
                  className={`${input} mt-3`}
                  placeholder="Please specify"
                  value={form.source_other}
                  onChange={(e) => setForm({ ...form, source_other: e.target.value })}
                />
              )}
            </div>

            {/* ---------- SUBMIT ---------- */}
            <div className="mt-12 pt-12 border-t border-[#D8D6D0]/10">
              <p className="text-xs text-[#D8D6D0]/40 mb-6 leading-relaxed">
                By submitting you agree to be contacted by NorthCrest Landscaping about your project. We never share your information.
              </p>
              <button
                type="submit"
                data-testid="est-submit"
                disabled={loading}
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Request Free Estimate <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        </FadeUp>

        {/* SIDE COLUMN */}
        <FadeUp delay={0.15} className="lg:col-span-4">
          <div className="nc-card p-8 lg:sticky lg:top-32">
            <div className="overline text-[#8C1D30] mb-3">What Happens Next</div>
            <h3 className="font-serif-display text-2xl mb-6">From request to design.</h3>
            <ol className="space-y-5 text-sm">
              <li className="flex gap-3">
                <span className="font-serif-display text-2xl text-[#8C1D30] leading-none w-8">01</span>
                <div>
                  <div className="text-[#F5F3EF] mb-1">Designer reviews your request</div>
                  <div className="text-[#D8D6D0]/60 text-xs leading-relaxed">Within 24 business hours.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-serif-display text-2xl text-[#8C1D30] leading-none w-8">02</span>
                <div>
                  <div className="text-[#F5F3EF] mb-1">On-site consultation</div>
                  <div className="text-[#D8D6D0]/60 text-xs leading-relaxed">Free site visit to assess slope, soil, sun, and access.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-serif-display text-2xl text-[#8C1D30] leading-none w-8">03</span>
                <div>
                  <div className="text-[#F5F3EF] mb-1">Custom proposal</div>
                  <div className="text-[#D8D6D0]/60 text-xs leading-relaxed">Detailed concept, materials, and itemized estimate.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-serif-display text-2xl text-[#8C1D30] leading-none w-8">04</span>
                <div>
                  <div className="text-[#F5F3EF] mb-1">Build begins</div>
                  <div className="text-[#D8D6D0]/60 text-xs leading-relaxed">Project scheduled, crew assigned, build starts on time.</div>
                </div>
              </li>
            </ol>

            <div className="mt-10 pt-6 border-t border-[#D8D6D0]/10">
              <div className="overline text-[#D8D6D0]/50 mb-3">Prefer to talk?</div>
              <a href="tel:+17806557604" className="block text-[#F5F3EF] font-serif-display text-2xl hover:text-[#8C1D30] transition-colors">(780) 655-7604</a>
              <a href="mailto:northcrestedmonton@gmail.com" className="block text-[#D8D6D0]/70 text-sm mt-2 link-underline">northcrestedmonton@gmail.com</a>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
